import { FetchError } from "@medusajs/js-sdk"
import {
  QueryKey,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query"
import qs from "qs"
import {
  AdminMerchantVerificationListResponse,
  AdminMerchantVerificationResponse,
} from "../../b2b/types"
import { queryKeysFactory } from "../../lib/query-key-factory"
import { sdk } from "../../lib/client"

export const merchantVerificationQueryKeys = queryKeysFactory(
  "merchant-verification"
)

export const useMerchantVerificationRequests = (
  query?: Record<string, any>,
  options?: Omit<
    UseQueryOptions<
      AdminMerchantVerificationListResponse,
      FetchError,
      AdminMerchantVerificationListResponse,
      QueryKey
    >,
    "queryKey" | "queryFn"
  >
) => {
  const filterQuery = qs.stringify(query ?? {}, {
    arrayFormat: "indices",
    encodeValuesOnly: true,
    skipNulls: true,
  })

  const fetchRequests = async () =>
    sdk.client.fetch<AdminMerchantVerificationListResponse>(
      `/admin/merchant-verification-requests${filterQuery ? `?${filterQuery}` : ""}`,
      {
        method: "GET",
      }
    )

  const { data, ...rest } = useQuery({
    queryKey: merchantVerificationQueryKeys.list(query),
    queryFn: fetchRequests,
    ...options,
  })
  return { ...data, ...rest }
}

export const useMerchantVerificationRequest = (
  requestId: string,
  options?: Omit<
    UseQueryOptions<
      AdminMerchantVerificationResponse,
      FetchError,
      AdminMerchantVerificationResponse,
      QueryKey
    >,
    "queryKey" | "queryFn"
  >
) => {
  const fetchRequest = async () =>
    sdk.client.fetch<AdminMerchantVerificationResponse>(
      `/admin/merchant-verification-requests/${requestId}`,
      {
        method: "GET",
      }
    )

  const { data, ...rest } = useQuery({
    queryKey: merchantVerificationQueryKeys.detail(requestId),
    queryFn: fetchRequest,
    ...options,
  })
  return { ...data, ...rest }
}

export const useApproveMerchantVerificationRequest = (
  requestId: string,
  options?: UseMutationOptions<
    { request: { id: string; status: string } },
    FetchError,
    void
  >
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () =>
      sdk.client.fetch<{ request: { id: string; status: string } }>(
        `/admin/merchant-verification-requests/${requestId}/approve`,
        {
          method: "POST",
        }
      ),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: merchantVerificationQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: merchantVerificationQueryKeys.detail(requestId),
      })
      options?.onSuccess?.(data as any, variables, context)
    },
    ...options,
  })
}

export const useRejectMerchantVerificationRequest = (
  requestId: string,
  options?: UseMutationOptions<
    { request: { id: string; status: string } },
    FetchError,
    { rejection_reason: string }
  >
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload) =>
      sdk.client.fetch<{ request: { id: string; status: string } }>(
        `/admin/merchant-verification-requests/${requestId}/reject`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
        }
      ),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: merchantVerificationQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: merchantVerificationQueryKeys.detail(requestId),
      })
      options?.onSuccess?.(data as any, variables, context)
    },
    ...options,
  })
}

export const useToggleMerchantCertificatePublic = (
  certificateId: string,
  requestId?: string,
  options?: UseMutationOptions<
    { certificate: { id: string } },
    FetchError,
    { is_public: boolean }
  >
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload) =>
      sdk.client.fetch(`/admin/merchant-certificates/${certificateId}/public`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
      }),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: merchantVerificationQueryKeys.lists(),
      })
      if (requestId) {
        queryClient.invalidateQueries({
          queryKey: merchantVerificationQueryKeys.detail(requestId),
        })
      }
      options?.onSuccess?.(data as any, variables, context)
    },
    ...options,
  })
}
